// BlitzPay.jsx - Complete Version with Multi-Recipient Batch Deduplication + Sibling Auto-Approval
// + BBC / Blitz Split routing — incoming (enterprise approval) + outgoing (SME management)
// FIXED: useBlitzPayData now exposes bbcOutgoingInvoices; pending modal uses it directly
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Snackbar,
  Button,
  Card,
  Typography,
  Alert,
  LinearProgress,
  IconButton,
  Chip,
  useMediaQuery,
  Collapse,
  Stack,
} from "@mui/material";
import {
  FilterList as FilterListIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import { useTheme, alpha } from "@mui/material/styles";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { CircularProgress } from "@mui/material";
import client from "../../API";
import useAuth from "../../Hooks/use-auth";
import { useBlitzPayData } from "./blitzpaypagecomponents/useBlitzPayData";
import TeamSpendingCard from "./blitzpaypagecomponents/TeamSpendingCard";
import SpendingDetailsModal from "./blitzpaypagecomponents/SpendingDetailsModal";
import NoFundsAlert from "./blitzpaypagecomponents/NoFundsAlert";
import PendingItemsModal from "./blitzpaypagecomponents/PendingItemsModal";
import BalanceLedgerModal from "./blitzpaypagecomponents/BlitzPayBalanceDialog";
import BlitzPayHeader from "./blitzpaypagecomponents/BlitzPayHeader";
import BlitzPayFilters from "./blitzpaypagecomponents/BlitzPayFilters";
import BlitzPaySummaryCards from "./blitzpaypagecomponents/BlitzPaySummaryCards";
import BlitzPayTabs from "./blitzpaypagecomponents/BlitzPayTabs";
import InvoiceEdit from "../Dashboard/invoicing/editInvoice";
import VendorDialog from "./vendorintake";
import UnifiedInvoiceActionDialog from "./extras/unifiedinvoiceaction";
import InvoicePaymentDialog from "./extras/clientCreatorInvoiceSettlement";
import BBCSplitApprovalDialog from "./BlitzSplit/Bbbcsplitapprovaldialog";
import BlitzSplitManageDialog from "./BlitzSplit/BbcSplitManageDialog";
import UnifiedActivityTable from "./tables/unifiedTableView";
import blitzLogo from "../../Components/globalAssets/platty.png";
import { StyledBadge } from "./blitzpaypagecomponents/styles";
import {
  formatAmount,
  getPaymentName as getPaymentNameHelper,
  calculateSummaryMetrics,
  getPendingOutgoingInvoices,
  getPendingIncomingInvoices,
  buildPendingItems,
  prepareCombinedActivity,
} from "./blitzpaypagecomponents/utils";

// ── Detect BBC / Blitz Split invoices ─────────────────────────────────────
const isBBCSplit = (item) =>
  item?.invoice_type === "bbc" ||
  item?.source_type === "bbc" ||
  item?.isBBC;

const BlitzPay = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { getCurrrentUser } = useAuth();
  const currentUser = getCurrrentUser();

  const {
    statusFilter, setStatusFilter,
    searchTerm, setSearchTerm,
    startDate, setStartDate,
    endDate, setEndDate,
    invoices, incomingInvoices, filteredInvoices,
    bbcOutgoingInvoices,
    campaignNames, payouts, isPayoutsLoading,
    spendingInfo, spendingLoading, spendingError,
    fetchInvoices, fetchIncomingInvoices, fetchPayouts, refetchSpending,
  } = useBlitzPayData();

  // ── UI state ──────────────────────────────────────────────────────────────
  const [tabIndex, setTabIndex]                       = useState(0);
  const [snackbarOpen, setSnackbarOpen]               = useState(false);
  const [snackbarMessage, setSnackbarMessage]         = useState("");
  const [editOpen, setEditOpen]                       = useState(false);
  const [currentInvoice, setCurrentInvoice]           = useState(null);
  const [vendorDialogOpen, setVendorDialogOpen]       = useState(false);
  const [spendingDetailsOpen, setSpendingDetailsOpen] = useState(false);
  const [noFundsAlertOpen, setNoFundsAlertOpen]       = useState(false);
  const [pendingModalOpen, setPendingModalOpen]       = useState(false);
  const [filtersOpen, setFiltersOpen]                 = useState(false);
  const [balanceLedgerOpen, setBalanceLedgerOpen]     = useState(false);
  const [unifiedActionDialogOpen, setUnifiedActionDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen]     = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId]     = useState(null);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState(null);
  const [currentPendingItemIndex, setCurrentPendingItemIndex]     = useState(0);
  const [allPendingItems, setAllPendingItems]         = useState([]);
  const [siblingInvoiceIds, setSiblingInvoiceIds]     = useState([]);
  const [isNavigatingPendingItems, setIsNavigatingPendingItems]   = useState(false);
  const hasShownPendingModalRef = useRef(false);

  // ── BBC incoming (enterprise approval) ───────────────────────────────────
  const [bbcDialogOpen, setBbcDialogOpen]             = useState(false);
  const [selectedBBCInvoice, setSelectedBBCInvoice]   = useState(null);

  // ── BBC outgoing (SME management) ────────────────────────────────────────
  const [bbcManageDialogOpen, setBbcManageDialogOpen]           = useState(false);
  const [selectedBBCManageInvoice, setSelectedBBCManageInvoice] = useState(null);

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: companyInfo } = useQuery(
    ["company", currentUser?.company_id],
    () => client.companies.listFetch({ id: currentUser?.company_id }),
    { enabled: !!currentUser?.company_id }
  );

  const hasBlitzPayAccount = companyInfo?.blitzpay_account === true;

  const {
    data: balanceLedgerData,
    isLoading: balanceLedgerLoading,
    isError: balanceLedgerError,
    error: balanceLedgerErrorObj,
  } = useQuery(
    ["balanceLedger", currentUser?.company_id],
    () => client.balanceLedger.fetchMyBalance(),
    { enabled: !!currentUser?.company_id }
  );

  const currentBalance       = balanceLedgerData?.balance        || 0;
  const balanceLedgerEntries = balanceLedgerData?.ledger_entries  || [];
  const transitBalance       = balanceLedgerData?.transit_balance || 0;

  const { data: ledgerHistoryData, isLoading: ledgerHistoryLoading } = useQuery(
    ["balanceLedgerHistory", currentUser?.company_id],
    () => client.balanceLedger.fetch(balanceLedgerData?.user?.id),
    { enabled: !!balanceLedgerData?.user?.id }
  );

  const { data: withdrawalHistoryData, isLoading: withdrawalHistoryLoading } = useQuery(
    ["withdrawalHistory", currentUser?.company_id],
    () => client.withdrawals.userGetHistory(),
    { enabled: !!currentUser?.company_id }
  );

  // ── Summary metrics ───────────────────────────────────────────────────────
  const summaryMetrics = calculateSummaryMetrics(invoices, payouts, incomingInvoices);
  const {
    creatorsPaid, amountInEscrow, paymentsThisMonth,
    totalIncomingAmount, pendingInvoices, pendingIncomingInvoices, pendingItems,
  } = summaryMetrics;

  const getPaymentName = (invoice) => getPaymentNameHelper(invoice, campaignNames);

  // ── Date-filtered incoming invoices ───────────────────────────────────────
  const filteredIncomingInvoices = incomingInvoices.filter(
    (inv) =>
      new Date(inv.payout_date || inv.created_at) >= new Date(startDate) &&
      new Date(inv.payout_date || inv.created_at) <= new Date(endDate)
  );

  const combinedInvoicesForInvoicesTab = [
    ...filteredInvoices.map((inv) => ({ ...inv, isIncoming: false })),
    ...filteredIncomingInvoices.map((inv) => ({ ...inv, isIncoming: true })),
  ].sort(
    (a, b) =>
      new Date(b.created_at || b.payout_date) -
      new Date(a.created_at || a.payout_date)
  );

  // ============================================================
  //  DEDUPLICATION — groups duplicate BlitzPay invoices by PO
  //  BBC invoices are excluded — each is its own receivable.
  // ============================================================
  const deduplicateInvoicePayoutPairs = (items) => {
    // BBC items pass through first — never deduplicated
    const bbcItems = items.filter((i) => isBBCSplit(i));

    const blitzpayInvoices = items.filter(
      (i) =>
        !isBBCSplit(i) &&
        (i.recordType === "blitzpay-invoice" || i.isBlitzPay)
    );
    const payoutItems = items.filter(
      (i) => i.recordType === "payout" || i.type === "Payout"
    );
    const otherItems = items.filter(
      (i) =>
        !isBBCSplit(i) &&
        i.recordType !== "blitzpay-invoice" &&
        !i.isBlitzPay &&
        i.recordType !== "payout" &&
        i.type !== "Payout"
    );

    const invoiceGroupsByPo = {};
    const invoicesWithoutPo = [];

    for (const inv of blitzpayInvoices) {
      const po = inv.po_number || inv.unique_code;
      if (po) {
        if (!invoiceGroupsByPo[po]) invoiceGroupsByPo[po] = [];
        invoiceGroupsByPo[po].push(inv);
      } else {
        invoicesWithoutPo.push(inv);
      }
    }

    const payoutsByPo = {};
    for (const p of payoutItems) {
      const po = p.po_number || p.unique_code;
      if (po) {
        if (!payoutsByPo[po]) payoutsByPo[po] = [];
        payoutsByPo[po].push(p);
      }
    }

    const usedPayoutIds  = new Set();
    const usedInvoiceIds = new Set();
    const mergedItems    = [];

    for (const [po, invoiceGroup] of Object.entries(invoiceGroupsByPo)) {
      invoiceGroup.forEach((inv) => usedInvoiceIds.add(inv.id));

      const totalInvAmount = invoiceGroup.reduce(
        (sum, inv) => sum + Number(inv.amount || inv.amount_due || 0),
        0
      );
      const primaryInvoice = invoiceGroup[0];

      const matchingPayouts = (payoutsByPo[po] || []).filter(
        (p) => !usedPayoutIds.has(p.id)
      );
      matchingPayouts.forEach((p) => usedPayoutIds.add(p.id));

      const isMulti         = invoiceGroup.length > 1 || matchingPayouts.length > 1;
      const isFunded        = primaryInvoice.status === "funded";
      const _firstPayout    = matchingPayouts[0];
      const _feeRate        = (_firstPayout?.interest_rate ?? 5) / 100;
      const _creatorPaysFee = _firstPayout?.role === "Creator";
      const _effectiveFeeRate = isFunded && _creatorPaysFee ? _feeRate : 0;
      const fee             = totalInvAmount * _effectiveFeeRate;

      const allDates = [
        new Date(primaryInvoice.date || primaryInvoice.created_at),
        ...matchingPayouts.map((p) => new Date(p.date || p.payout_date)),
      ];
      const latestDate = allDates.reduce((a, b) => (b > a ? b : a));

      const totalNetAmount   = matchingPayouts.reduce(
        (sum, p) => sum + Number(p.net_amount || p.amount || 0),
        0
      );
      const payoutDetailsArray = matchingPayouts.map((p) => ({
        payout_id:      p.id,
        creator_id:     p.creator_id,
        creator_info:   p.creator_info,
        name:           p.creator_info?.display_name || p.creator_id || "Creator",
        payout_date:    p.payout_date || p.date,
        payment_timing: p.payment_timing,
        blitzpay:       p.blitzpay,
        payout_method:  p.payout_method,
        net_amount:     Number(p.net_amount || p.amount || 0),
        fee_amount:     p.fee_amount,
        interest_rate:  p.interest_rate,
        role:           p.role,
        fee_payer:      p.fee_payer,
        status:         p.status,
        po_number:      p.po_number || p.unique_code,
      }));

      mergedItems.push({
        ...primaryInvoice,
        siblingInvoiceIds:    invoiceGroup.slice(1).map((inv) => inv.id),
        amount:               totalInvAmount,
        amount_due:           totalInvAmount,
        isMerged:             matchingPayouts.length > 0,
        isMultiRecipient:     isMulti,
        recipientCount:       Math.max(invoiceGroup.length, matchingPayouts.length),
        linkedPayoutIds:      matchingPayouts.map((p) => p.id),
        payoutDetailsArray,
        payoutDetails:        payoutDetailsArray[0] || null,
        totalPayoutNetAmount: totalNetAmount,
        feeBreakdown: isFunded
          ? {
              invoiceAmount:  totalInvAmount,
              platformFee:    fee,
              totalCharge:    totalInvAmount + fee,
              feePercentage:  _effectiveFeeRate * 100,
              isFunded:       true,
            }
          : null,
        date:   latestDate.toISOString(),
        status: isFunded
          ? "funded"
          : matchingPayouts[0]?.status || primaryInvoice.status,
      });
    }

    for (const invoice of invoicesWithoutPo) {
      if (usedInvoiceIds.has(invoice.id)) continue;
      usedInvoiceIds.add(invoice.id);

      const invAmount = Number(invoice.amount || invoice.amount_due || 0);
      const invDate   = new Date(invoice.date || invoice.created_at);

      const fallback =
        invAmount > 0
          ? payoutItems.find((p) => {
              if (usedPayoutIds.has(p.id)) return false;
              const pa    = Number(p.amount || p.net_amount || 0);
              if (pa === 0) return false;
              const adiff = Math.abs(pa - invAmount) / invAmount;
              const ddiff = Math.abs(
                (invDate - new Date(p.date || p.payout_date)) / 86400000
              );
              return adiff <= 0.1 && ddiff <= 7;
            })
          : null;

      if (fallback) {
        usedPayoutIds.add(fallback.id);
        const isFunded          = invoice.status === "funded";
        const _fbFeeRate        = (fallback.interest_rate ?? 5) / 100;
        const _fbCreatorPaysFee = fallback.role === "Creator";
        const _fbEffectiveFeeRate = isFunded && _fbCreatorPaysFee ? _fbFeeRate : 0;
        const fee = invAmount * _fbEffectiveFeeRate;
        const pd  = {
          payout_id:      fallback.id,
          creator_id:     fallback.creator_id,
          creator_info:   fallback.creator_info,
          name:           fallback.creator_info?.display_name || fallback.creator_id || "Creator",
          payout_date:    fallback.payout_date || fallback.date,
          payment_timing: fallback.payment_timing,
          blitzpay:       fallback.blitzpay,
          payout_method:  fallback.payout_method,
          net_amount:     Number(fallback.net_amount || fallback.amount || 0),
          fee_amount:     fallback.fee_amount,
          interest_rate:  fallback.interest_rate,
          role:           fallback.role,
          fee_payer:      fallback.fee_payer,
          status:         fallback.status,
          po_number:      fallback.po_number || fallback.unique_code,
        };
        mergedItems.push({
          ...invoice,
          isMerged:             true,
          isMultiRecipient:     false,
          recipientCount:       1,
          linkedPayoutIds:      [fallback.id],
          payoutDetailsArray:   [pd],
          payoutDetails:        pd,
          totalPayoutNetAmount: pd.net_amount,
          feeBreakdown: isFunded
            ? {
                invoiceAmount:  invAmount,
                platformFee:    fee,
                totalCharge:    invAmount + fee,
                feePercentage:  _fbEffectiveFeeRate * 100,
                isFunded:       true,
              }
            : null,
          date: new Date(
            Math.max(invDate, new Date(fallback.date || fallback.payout_date))
          ).toISOString(),
          status: isFunded ? "funded" : fallback.status || invoice.status,
        });
      } else {
        mergedItems.push(invoice);
      }
    }

    const unmatchedPayouts = payoutItems.filter((p) => !usedPayoutIds.has(p.id));
    return [...bbcItems, ...mergedItems, ...unmatchedPayouts, ...otherItems];
  };

  // ── Enrich records ────────────────────────────────────────────────────────
  const enrichedPayouts = payouts.map((p) => {
    const ci          = p.creator_info || {};
    const creatorId   = p.creator_id || ci.creator_id;
    const displayName = ci.display_name || p.creator_display_name || p.name || creatorId;
    const payoutDate  = p.payout_date || p.date || p.created_at;
    return {
      ...p,
      recordType:  "payout",
      type:        "Payout",
      creator_id:  creatorId,
      creator_info: {
        display_name: displayName,
        creator_id:   creatorId,
        email:        ci.email || p.email,
        phone_number: ci.phone_number || p.phone_number,
        payout_email: ci.payout_email,
        is_vendor:    ci.is_vendor,
        status:       ci.status,
      },
      name:           displayName,
      amount:         p.amount || 0,
      net_amount:     p.net_amount || p.amount || 0,
      date:           payoutDate,
      payout_date:    payoutDate,
      po_number:      p.po_number || p.unique_code || `#${p.id}`,
      status:         p.status || "pending",
      blitzpay:       p.blitzpay || false,
      payment_timing: p.payment_timing || (p.blitzpay ? "instant" : "net30"),
      role:           p.role,
      fee_payer:      p.fee_payer,
    };
  });

  // DEBUG: log what filteredInvoices contains before enrichment
  const _bbcInFiltered = filteredInvoices.filter(inv => inv.invoice_type === 'bbc' || inv.source_type === 'bbc' || inv.isBBC);
  if (_bbcInFiltered.length > 0 || filteredInvoices.length > 0) {
    console.log('[BlitzPay] filteredInvoices total:', filteredInvoices.length, 'BBC:', _bbcInFiltered.length);
    _bbcInFiltered.forEach(inv => console.log('[BlitzPay] BBC in filteredInvoices:', {
      id: inv.id, invoice_type: inv.invoice_type, source_type: inv.source_type,
      isBBC: inv.isBBC, status: inv.status, campaign_name: inv.campaign_name,
    }));
  }

  const enrichedFilteredInvoices = filteredInvoices.map((inv) => {
    const isBlitzPay = inv.invoice_type === "blitzpay" || inv.source_type === "blitzpay";
    const isBBC      = inv.invoice_type === "bbc"      || inv.source_type === "bbc";
    let displayName  = inv.campaign_name || inv.name;
    if (!displayName) displayName = getPaymentName(inv) || "Invoice";
    // Use campaign_name from backend if it was already enriched (BBC branch sets it)
    const finalDisplayName = inv.campaign_name || displayName;
    const dateValue  = inv.created_at || inv.payout_date || inv.date || new Date().toISOString();
    return {
      ...inv,
      recordType:    isBBC ? "bbc-invoice" : isBlitzPay ? "blitzpay-invoice" : "invoice",
      type:          isBBC ? "Blitz Split"  : isBlitzPay ? "BlitzPay Payment" : "Invoice",
      isIncoming:    false,
      isBlitzPay:    isBlitzPay,
      isBBC:         isBBC,
      invoice_type:  inv.invoice_type,
      source_type:   inv.source_type,
      name:          finalDisplayName,
      campaign_name: finalDisplayName,
      amount:        inv.amount_due || inv.amount,
      // Explicitly set all three date fields so combinedActivityRaw gate always
      // finds a date regardless of which key it checks
      date:          dateValue,
      created_at:    inv.created_at || dateValue,
      payout_date:   inv.payout_date || inv.created_at || dateValue,
      status:        inv.status || "pending",
    };
  });

  const enrichedIncomingInvoices = filteredIncomingInvoices.map((inv) => {
    const dateValue = inv.created_at || inv.payout_date || inv.date || new Date().toISOString();
    return {
      ...inv,
      recordType:   "invoice",
      type:         "Invoice",
      isIncoming:   true,
      name:         inv.client_name || "Incoming Invoice",
      amount:       inv.amount || inv.amount_due,
      status:       inv.invoice_status || inv.status,
      date:         dateValue,
      created_at:   inv.created_at || dateValue,
      payout_date:  inv.payout_date || inv.created_at || dateValue,
      invoice_type: inv.invoice_type,
      source_type:  inv.source_type,
      isBBC:        inv.invoice_type === "bbc" || inv.source_type === "bbc",
      payment_term: inv.payment_term,
    };
  });

  // Ledger entries: only show EXPLICIT internal transfers the user initiated
  // (operation_type === 'internal_transfer'). Do NOT include blitzpay === true
  // entries — those are payout_credit / balance_debit ops that belong in the
  // Balance Ledger modal, not the main activity feed. Including them floods the
  // feed with "Balance Transfer" cards that crowd out actual invoices.
  const enrichedLedgerEntries = (ledgerHistoryData || [])
    .filter(
      (e) =>
        e.transaction_type === "internal_transfer" ||
        e.operation_type  === "internal_transfer"
    )
    .map((entry) => ({
      ...entry,
      recordType:  "transfer",
      type:        "Internal Transfer",
      name:        entry.brief || entry.reason || "Balance Transfer",
      amount:      Math.abs(parseFloat(entry.change_amount || entry.amount || 0)),
      date:        entry.created_at || entry.timestamp,
      status:      entry.status || "completed",
      po_number:   `TXN-${entry.id}`,
      direction:
        entry.direction ||
        (parseFloat(entry.change_amount || 0) > 0 ? "credit" : "debit"),
      description: entry.brief || entry.description || "Internal transfer",
    }));

  const enrichedWithdrawals = (withdrawalHistoryData?.withdrawals || []).map(
    (w) => ({
      ...w,
      recordType:      "withdrawal",
      type:            "Withdrawal",
      name:            `Withdrawal to ${w.payment_service || "Bank"}`,
      amount:          Math.abs(parseFloat(w.amount || w.amount_no_fee || 0)),
      date:            w.created_at,
      status:          w.status || "pending",
      po_number:       `WD-${w.id}`,
      direction:       "debit",
      payment_service: w.payment_service,
      description:     w.notes || "Withdrawal",
    })
  );

  // DEBUG: log enrichedFilteredInvoices BBC result
  const _bbcEnriched = enrichedFilteredInvoices.filter(inv => inv.isBBC);
  console.log('[BlitzPay] enrichedFilteredInvoices total:', enrichedFilteredInvoices.length, 'BBC enriched:', _bbcEnriched.length);
  _bbcEnriched.forEach(inv => console.log('[BlitzPay] BBC enriched:', {
    id: inv.id, recordType: inv.recordType, type: inv.type,
    isBBC: inv.isBBC, isIncoming: inv.isIncoming, status: inv.status,
    invoice_type: inv.invoice_type, source_type: inv.source_type,
    name: inv.name, campaign_name: inv.campaign_name,
  }));

  // DEBUG: log dedup result
  // (logged after dedup below)

  // ── Combine + deduplicate + filter ────────────────────────────────────────
  const combinedActivityRaw = [
    ...enrichedFilteredInvoices,
    ...enrichedIncomingInvoices,
    ...enrichedPayouts,
    ...enrichedLedgerEntries,
    ...enrichedWithdrawals,
  ].filter((item) => {
    const d = item.payout_date || item.created_at || item.date;
    if (!d) return false;
    const date = new Date(d);
    return date >= new Date(startDate) && date <= new Date(endDate);
  });

  const combinedActivityUnified = deduplicateInvoicePayoutPairs(
    combinedActivityRaw
  ).sort(
    (a, b) =>
      new Date(b.payout_date || b.created_at || b.date) -
      new Date(a.payout_date || a.created_at || a.date)
  );

  // DEBUG: log BBC survival through dedup and sort
  const _bbcInUnified = combinedActivityUnified.filter(inv => inv.isBBC || inv.invoice_type === 'bbc' || inv.source_type === 'bbc');
  console.log('[BlitzPay] combinedActivityUnified total:', combinedActivityUnified.length, 'BBC:', _bbcInUnified.length);
  _bbcInUnified.forEach(inv => console.log('[BlitzPay] BBC in unified:', {
    id: inv.id, recordType: inv.recordType, isBBC: inv.isBBC, isIncoming: inv.isIncoming,
    invoice_type: inv.invoice_type, status: inv.status, name: inv.name,
  }));

  const filteredCombinedActivity = combinedActivityUnified.filter(
    (item) =>
      searchTerm === "" ||
      (item.name      || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.po_number || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.status    || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ── Mutations ─────────────────────────────────────────────────────────────
  const { mutate: unifiedInvoiceAction } = useMutation(
    client.invoices.unifiedAction,
    {
      onSuccess: (data) => {
        setSnackbarMessage(data.message || "Invoice action completed successfully.");
        setSnackbarOpen(true);
        fetchIncomingInvoices();
        fetchInvoices();
        fetchPayouts();
        refetchSpending();
      },
      onError: (error) => {
        setSnackbarMessage(
          error.response?.data?.error || "Error performing invoice action."
        );
        setSnackbarOpen(true);
      },
    }
  );

  const { mutate: mergeBlitzPays, isLoading: isMerging } = useMutation(
    (invoiceIds) => client.bank.mergeBlitzPayInvoices({ invoice_ids: invoiceIds }),
    {
      onSuccess: (data) => {
        setSnackbarMessage(data.message || "BlitzPays merged successfully.");
        setSnackbarOpen(true);
        fetchInvoices();
        refetchSpending();
      },
      onError: (error) => {
        setSnackbarMessage(
          error.response?.data?.error || "Error merging BlitzPays."
        );
        setSnackbarOpen(true);
      },
    }
  );

  const handleMergeBlitzPays = (invoiceIds) => {
    if (invoiceIds.length < 2) return;
    mergeBlitzPays(invoiceIds);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const showSnack = (msg) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  };

  const getInvoiceDialogType = (item) => {
    if (item.isIncoming && isBBCSplit(item))  return "bbc";
    if (!item.isIncoming && isBBCSplit(item)) return "bbc-manage";

    const invoiceType = item.invoice_type || item.recordType;
    const isBlitzPay  =
      invoiceType === "blitzpay" ||
      item.source_type === "blitzpay" ||
      item.isBlitzPay;
    if (isBlitzPay)      return "unified";
    if (item.isIncoming) return "payment";
    return "payment";
  };

  // ── Event handlers ────────────────────────────────────────────────────────
  const handleCopyLink = () => {
    const fullUrl =
      window.location.origin +
      `/vendor_signup/${encodeURIComponent(currentUser?.company_name || "")}`;
    navigator.clipboard
      .writeText(fullUrl)
      .then(() => showSnack("Vendor Signup Link copied to clipboard!"))
      .catch(console.error);
  };

  const handleEditDialogOpen = (invoice) => {
    if (
      invoice.invoice_type === "blitzpay" ||
      invoice.source_type  === "blitzpay" ||
      invoice.isBlitzPay
    ) {
      showSnack("BlitzPay payments cannot be edited");
      return;
    }
    if (isBBCSplit(invoice)) {
      showSnack("Blitz Split invoices cannot be edited after creation");
      return;
    }
    if (
      ["paid", "completed", "funded"].includes(
        (invoice.status || "").toLowerCase()
      )
    ) {
      showSnack("Completed invoices cannot be edited");
      return;
    }
    setCurrentInvoice(invoice);
    setEditOpen(true);
  };

  const handleEditDialogClose = (refresh = false) => {
    setEditOpen(false);
    setCurrentInvoice(null);
    if (refresh) fetchInvoices();
  };

  const handleUnifiedInvoiceAction = (invoiceIdOrItem) => {
    let item =
      typeof invoiceIdOrItem === "object"
        ? invoiceIdOrItem
        : [...enrichedFilteredInvoices, ...enrichedIncomingInvoices].find(
            (inv) => inv.id === invoiceIdOrItem
          );

    if (!item) { showSnack("Invoice not found"); return; }

    const status = (item.status || item.invoice_status || "").toLowerCase();
    const isOutgoingBBC = !item.isIncoming && isBBCSplit(item);

    if (!isOutgoingBBC) {
      if (item.isIncoming && isBBCSplit(item)) {
        if (status !== "pending") {
          showSnack("This Split has already been processed");
          return;
        }
      } else {
        if (["paid", "completed", "funded", "approved"].includes(status)) {
          showSnack("This invoice has already been processed");
          return;
        }
        if (status !== "pending") {
          showSnack("No actions available for this invoice");
          return;
        }
      }
    }

    setSelectedInvoiceId(item.id);

    const isBlitzPayItem =
      item.isBlitzPay ||
      item.invoice_type === "blitzpay" ||
      item.source_type  === "blitzpay" ||
      item.recordType   === "blitzpay-invoice";

    if (item.po_number && isBlitzPayItem && !isBBCSplit(item)) {
      const allKnownInvoices = [
        ...enrichedFilteredInvoices,
        ...enrichedIncomingInvoices,
      ];
      const siblings = allKnownInvoices.filter(
        (inv) =>
          inv.id !== item.id &&
          inv.po_number === item.po_number &&
          (inv.status || inv.invoice_status || "").toLowerCase() === "pending"
      );
      setSiblingInvoiceIds(siblings.map((s) => s.id));
    } else {
      setSiblingInvoiceIds([]);
    }

    const dialogType = getInvoiceDialogType(item);
    if (dialogType === "bbc") {
      setSelectedBBCInvoice(item);
      setBbcDialogOpen(true);
    } else if (dialogType === "bbc-manage") {
      setSelectedBBCManageInvoice(item);
      setBbcManageDialogOpen(true);
    } else if (dialogType === "payment") {
      setSelectedInvoiceForPayment(item);
      setPaymentDialogOpen(true);
    } else {
      setUnifiedActionDialogOpen(true);
    }
  };

  const handlePendingItemAction = (item, itemIndex = null) => {
    setIsNavigatingPendingItems(true);
    if (itemIndex !== null) setCurrentPendingItemIndex(itemIndex);
    setPendingModalOpen(false);
    handleUnifiedInvoiceAction(item);
  };

  const navigatePending = (delta) => {
    const newIndex =
      (currentPendingItemIndex + delta + allPendingItems.length) %
      allPendingItems.length;
    setCurrentPendingItemIndex(newIndex);
    const nextItem = allPendingItems[newIndex];
    setSelectedInvoiceId(nextItem.id);

    const dialogType = getInvoiceDialogType(nextItem);
    if (dialogType === "bbc") {
      setSelectedBBCInvoice(nextItem);
      setUnifiedActionDialogOpen(false);
      setPaymentDialogOpen(false);
      setBbcManageDialogOpen(false);
      setSelectedBBCManageInvoice(null);
      setBbcDialogOpen(true);
    } else if (dialogType === "bbc-manage") {
      setSelectedBBCManageInvoice(nextItem);
      setUnifiedActionDialogOpen(false);
      setPaymentDialogOpen(false);
      setBbcDialogOpen(false);
      setSelectedBBCInvoice(null);
      setBbcManageDialogOpen(true);
    } else if (dialogType === "payment") {
      setSelectedInvoiceForPayment(nextItem);
      setUnifiedActionDialogOpen(false);
      setBbcDialogOpen(false);
      setSelectedBBCInvoice(null);
      setBbcManageDialogOpen(false);
      setSelectedBBCManageInvoice(null);
      setPaymentDialogOpen(true);
    } else {
      setPaymentDialogOpen(false);
      setBbcDialogOpen(false);
      setSelectedBBCInvoice(null);
      setBbcManageDialogOpen(false);
      setSelectedBBCManageInvoice(null);
      setUnifiedActionDialogOpen(true);
    }
  };

  const handleBackToPendingList = () => {
    setUnifiedActionDialogOpen(false);
    setPaymentDialogOpen(false);
    setBbcDialogOpen(false);
    setSelectedBBCInvoice(null);
    setBbcManageDialogOpen(false);
    setSelectedBBCManageInvoice(null);
    setPendingModalOpen(true);
    setIsNavigatingPendingItems(false);
  };

  const handleCloseAllPendingDialogs = () => {
    setUnifiedActionDialogOpen(false);
    setPaymentDialogOpen(false);
    setBbcDialogOpen(false);
    setSelectedBBCInvoice(null);
    setBbcManageDialogOpen(false);
    setSelectedBBCManageInvoice(null);
    setPendingModalOpen(false);
    setIsNavigatingPendingItems(false);
    setCurrentPendingItemIndex(0);
    setAllPendingItems([]);
    setSiblingInvoiceIds([]);
  };

  const handleActionSuccess = (message) => {
    showSnack(message || "Action completed successfully");
    fetchIncomingInvoices();
    fetchInvoices();
    fetchPayouts();
    refetchSpending();

    if (isNavigatingPendingItems && allPendingItems.length > 1) {
      const updatedItems = allPendingItems.filter(
        (_, i) => i !== currentPendingItemIndex
      );
      setAllPendingItems(updatedItems);
      if (updatedItems.length === 0) {
        handleCloseAllPendingDialogs();
      } else {
        const newIndex =
          currentPendingItemIndex >= updatedItems.length
            ? 0
            : currentPendingItemIndex;
        setCurrentPendingItemIndex(newIndex);
        setPendingModalOpen(true);
        setUnifiedActionDialogOpen(false);
        setPaymentDialogOpen(false);
        setBbcDialogOpen(false);
        setSelectedBBCInvoice(null);
        setBbcManageDialogOpen(false);
        setSelectedBBCManageInvoice(null);
      }
    } else {
      handleCloseAllPendingDialogs();
    }
  };

  /**
   * handleApproveAction — unified approve/manage handler.
   * Outgoing BBC (SME): always open manage dialog, any status.
   * Everything else: only fire on pending status.
   */
  const handleApproveAction = (itemOrId, sourceList) => {
    const list = sourceList || filteredCombinedActivity;
    const item =
      typeof itemOrId === "object"
        ? itemOrId
        : list.find((i) => i.id === itemOrId);
    if (!item) return;

    const s = (item.status || item.invoice_status || "").toLowerCase();
    const outgoingBBC = !item.isIncoming && isBBCSplit(item);

    if (outgoingBBC) {
      handleUnifiedInvoiceAction(item);
      return;
    }

    if (s === "pending") {
      handleUnifiedInvoiceAction(item);
    } else {
      showSnack("This item cannot be actioned");
    }
  };

  const viewHandler = (item) => {
    const t = item.type || item.recordType;
    if (
      t === "Payout" ||
      t === "payout" ||
      t === "Internal Transfer" ||
      t === "Withdrawal"
    )
      return;
    handleUnifiedInvoiceAction(item);
  };

  const downloadPDF = () => {
    const doc       = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.addImage(blitzLogo, "PNG", (pageWidth - 30) / 2, 10, 30, 20);
    doc.setFontSize(18);
    doc.text("BlitzPay Report", pageWidth / 2, 40, { align: "center" });
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, 48, { align: "center" });

    let y = 60;
    doc.setFontSize(14);
    doc.text("Financial Summary", 14, y);
    y += 10;
    doc.autoTable({
      startY: y,
      head: [["Metric", "Value"]],
      body: [
        hasBlitzPayAccount && ["Total Available Funds", `$${spendingInfo?.total_available_funds || 0}`],
        ["Current Balance",     formatAmount(currentBalance)],
        ["Creators Paid",       `${creatorsPaid}`],
        ["Payments This Month", formatAmount(paymentsThisMonth)],
        ["Pending Items",       `${pendingItems}`],
      ].filter(Boolean),
      theme:      "grid",
      styles:     { fontSize: 10 },
      headStyles: { fillColor: [63, 81, 181] },
    });

    y = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(14);
    doc.text("Activity Details", 14, y);
    y += 10;
    doc.autoTable({
      startY: y,
      head:   [["Date", "Name", "PO #", "Type", "Amount", "Status"]],
      body:   filteredCombinedActivity.map((item) => [
        new Date(item.date).toLocaleDateString(),
        item.name,
        item.po_number,
        item.isMultiRecipient ? `BlitzPay (${item.recipientCount} creators)` : item.type,
        formatAmount(item.amount),
        item.status,
      ]),
      theme:      "striped",
      styles:     { fontSize: 9 },
      headStyles: { fillColor: [63, 81, 181] },
    });

    doc.save(`BlitzPay_Report_${new Date().toISOString().split("T")[0]}.pdf`);
    showSnack("PDF downloaded successfully!");
  };

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (spendingInfo && !spendingInfo.has_available_funds && hasBlitzPayAccount) {
      setNoFundsAlertOpen(true);
    }
  }, [spendingInfo, hasBlitzPayAccount]);

  useEffect(() => {
    if (
      !hasShownPendingModalRef.current &&
      (invoices.length > 0 || incomingInvoices.length > 0)
    ) {
      // Standard pending outgoing BlitzPay invoices
      const standardPendingOut = getPendingOutgoingInvoices(invoices);
      const standardPendingIds = new Set(standardPendingOut.map((i) => i.id));

      // Outgoing BBC — use pre-derived slice from hook (already excludes cancelled)
      const bbcOutgoing = (bbcOutgoingInvoices || []).filter(
        (inv) => !standardPendingIds.has(inv.id)
      );

      const rawPendingOut = [...standardPendingOut, ...bbcOutgoing];

      // Deduplicate BlitzPay by PO; BBC invoices always shown individually
      const seenPendingPOs = new Set();
      const pendingOut = rawPendingOut.filter((inv) => {
        if (isBBCSplit(inv)) return true;
        const po = inv.po_number;
        if (po) {
          if (seenPendingPOs.has(po)) return false;
          seenPendingPOs.add(po);
        }
        return true;
      });

      const pendingIn = getPendingIncomingInvoices(incomingInvoices);

      if (pendingOut.length + pendingIn.length > 0) {
        hasShownPendingModalRef.current = true;
        const list = buildPendingItems({
          pendingInvoices:  pendingOut,
          incomingInvoices: pendingIn,
          getPaymentName,
        });
        setAllPendingItems(list);
        setCurrentPendingItemIndex(0);
        setTimeout(() => setPendingModalOpen(true), 300);
      }
    }
  }, [invoices, incomingInvoices, bbcOutgoingInvoices, getPaymentName]);

  // ── Loading / error screens ───────────────────────────────────────────────
  if (spendingLoading) {
    return (
      <Box sx={{ p: 3, bgcolor: "background.default", minHeight: "100vh", height: "100%" }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          BlitzPay
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", mt: 5 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading your team spending power information...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (spendingError && !spendingInfo) {
    return (
      <Box sx={{ p: 3, bgcolor: "background.default", minHeight: "100vh" }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          BlitzPay
        </Typography>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Unable to load your team spending power information.
          </Typography>
        </Alert>
        <Button variant="contained" onClick={() => navigate("/user")}>
          Go to Team Settings
        </Button>
      </Box>
    );
  }

  // ── Shared dialogs ────────────────────────────────────────────────────────
  const sharedModals = (
    <>
      <PendingItemsModal
        open={pendingModalOpen}
        onClose={() => setPendingModalOpen(false)}
        pendingInvoices={pendingInvoices}
        incomingInvoices={pendingIncomingInvoices}
        formatAmount={formatAmount}
        onItemAction={handlePendingItemAction}
        getPaymentName={getPaymentName}
      />

      {hasBlitzPayAccount && (
        <>
          <SpendingDetailsModal
            open={spendingDetailsOpen}
            onClose={() => setSpendingDetailsOpen(false)}
            spendingInfo={spendingInfo}
          />
          <NoFundsAlert
            open={noFundsAlertOpen}
            onClose={() => setNoFundsAlertOpen(false)}
            spendingInfo={spendingInfo}
            currentBalance={currentBalance}
          />
        </>
      )}

      <BalanceLedgerModal
        open={balanceLedgerOpen}
        onClose={() => setBalanceLedgerOpen(false)}
        ledgerEntries={balanceLedgerEntries}
        isLoading={balanceLedgerLoading}
        isError={balanceLedgerError}
        error={balanceLedgerErrorObj}
        currentBalance={currentBalance}
        transitBalance={transitBalance}
      />

      {currentInvoice && (
        <InvoiceEdit
          open={editOpen}
          onClose={handleEditDialogClose}
          invoiceInfo={currentInvoice}
        />
      )}

      <VendorDialog
        open={vendorDialogOpen}
        onClose={() => setVendorDialogOpen(false)}
      />

      <InvoicePaymentDialog
        open={paymentDialogOpen}
        onClose={
          isNavigatingPendingItems
            ? handleBackToPendingList
            : handleCloseAllPendingDialogs
        }
        invoice={selectedInvoiceForPayment}
        onSuccess={(r) =>
          handleActionSuccess(r?.message || "Payment processed successfully!")
        }
      />

      <UnifiedInvoiceActionDialog
        open={unifiedActionDialogOpen}
        siblingInvoiceIds={siblingInvoiceIds}
        onClose={
          isNavigatingPendingItems
            ? handleBackToPendingList
            : handleCloseAllPendingDialogs
        }
        invoiceId={selectedInvoiceId}
        onSuccess={handleActionSuccess}
        unifiedInvoiceActionMutation={unifiedInvoiceAction}
        showNavigation={isNavigatingPendingItems && allPendingItems.length > 1}
        currentItemIndex={currentPendingItemIndex + 1}
        totalItems={allPendingItems.length}
        onNavigateNext={allPendingItems.length > 1 ? () => navigatePending(1)  : null}
        onNavigatePrevious={allPendingItems.length > 1 ? () => navigatePending(-1) : null}
        onBackToList={handleBackToPendingList}
        currentItemName={allPendingItems[currentPendingItemIndex]?.name || ""}
      />

      <BBCSplitApprovalDialog
        open={bbcDialogOpen}
        onClose={
          isNavigatingPendingItems
            ? handleBackToPendingList
            : () => {
                setBbcDialogOpen(false);
                setSelectedBBCInvoice(null);
              }
        }
        invoice={selectedBBCInvoice}
        onSuccess={(message) => {
          handleActionSuccess(message);
          setBbcDialogOpen(false);
          setSelectedBBCInvoice(null);
        }}
      />

      <BlitzSplitManageDialog
        open={bbcManageDialogOpen}
        onClose={() => {
          setBbcManageDialogOpen(false);
          setSelectedBBCManageInvoice(null);
        }}
        invoice={selectedBBCManageInvoice}
        onSuccess={(message) => {
          handleActionSuccess(message);
          setBbcManageDialogOpen(false);
          setSelectedBBCManageInvoice(null);
        }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  );

  const payoutsTabItems = enrichedPayouts.filter((p) => {
    const d = p.payout_date || p.date;
    if (!d) return false;
    const dateOk =
      new Date(d) >= new Date(startDate) && new Date(d) <= new Date(endDate);
    const statusOk = !statusFilter || p.status === statusFilter;
    const searchOk =
      !searchTerm ||
      (p.name       || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.po_number  || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.creator_id || "").toLowerCase().includes(searchTerm.toLowerCase());
    return dateOk && statusOk && searchOk;
  });

  const activityLoading =
    isPayoutsLoading || ledgerHistoryLoading || withdrawalHistoryLoading;

  // ── Mobile ────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <Box sx={{ minHeight: "100vh", pb: 3, bgcolor: "background.default" }}>
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <BlitzPayHeader
              totalIncomingAmount={totalIncomingAmount}
              spendingInfo={spendingInfo}
              companyInfo={companyInfo}
              onCopyLink={handleCopyLink}
              onOpenVendorDialog={() => setVendorDialogOpen(true)}
            />
          </Box>

          <Box sx={{ px: 2, py: 1.5 }} data-tour="payment-dashboard">
            <BlitzPaySummaryCards
              spendingInfo={spendingInfo}
              spendingLoading={spendingLoading}
              pendingItems={pendingItems}
              amountInEscrow={amountInEscrow}
              creatorsPaid={creatorsPaid}
              currentBalance={currentBalance}
              onShowSpendingDetails={() => setSpendingDetailsOpen(true)}
              onShowPendingItems={() => setPendingModalOpen(true)}
              onShowBalanceLedger={() => setBalanceLedgerOpen(true)}
              hasBlitzPayAccount={hasBlitzPayAccount}
              companyInfo={companyInfo}
            />
          </Box>

          <Box sx={{ px: 2 }} data-tour="transaction-history">
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    endIcon={filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setFiltersOpen(!filtersOpen)}
                    fullWidth
                    sx={{
                      borderRadius: 2, textTransform: "none",
                      justifyContent: "flex-start", py: 1,
                      borderColor: "divider", fontSize: "0.813rem", fontWeight: 500,
                    }}
                  >
                    Filters
                    {(statusFilter || searchTerm) && (
                      <Chip
                        label="Active" size="small" color="primary"
                        sx={{ ml: "auto", mr: 1, height: 20, fontSize: "0.7rem" }}
                      />
                    )}
                  </Button>
                  <Collapse in={filtersOpen}>
                    <Box sx={{ mt: 1.5 }}>
                      <BlitzPayFilters
                        searchTerm={searchTerm}    setSearchTerm={setSearchTerm}
                        statusFilter={statusFilter} setStatusFilter={setStatusFilter}
                        startDate={startDate}       setStartDate={setStartDate}
                        endDate={endDate}           setEndDate={setEndDate}
                        onDownloadPDF={downloadPDF}
                        isMobile={true}
                      />
                    </Box>
                  </Collapse>
                </Box>
              </Box>

              <BlitzPayTabs
                tabIndex={tabIndex}
                onTabChange={(e, v) => setTabIndex(v)}
                totalIncomingAmount={totalIncomingAmount}
                isMobile={true}
              />

              {tabIndex === 0 && (
                <Box sx={{ p: 2 }}>
                  <UnifiedActivityTable
                    items={filteredCombinedActivity}
                    onEdit={handleEditDialogOpen}
                    onApprove={(itemOrId) =>
                      handleApproveAction(itemOrId, filteredCombinedActivity)
                    }
                    onView={viewHandler}
                    onMerge={handleMergeBlitzPays}
                    isMerging={isMerging}
                    isLoading={activityLoading}
                    isMobile={true}
                    defaultView="cards"
                  />
                </Box>
              )}

              {tabIndex === 1 && (
                <Box sx={{ p: 2 }}>
                  {totalIncomingAmount > 0 && (
                    <Box
                      sx={{
                        mb: 2, p: 1.5, borderRadius: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <InfoIcon sx={{ color: "info.main", fontSize: 18, flexShrink: 0 }} />
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.813rem" }}>
                          {totalIncomingAmount} invoice{totalIncomingAmount !== 1 ? "s" : ""} awaiting approval
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                  <UnifiedActivityTable
                    items={combinedInvoicesForInvoicesTab}
                    onEdit={handleEditDialogOpen}
                    onApprove={handleUnifiedInvoiceAction}
                    onView={handleUnifiedInvoiceAction}
                    onMerge={handleMergeBlitzPays}
                    isMerging={isMerging}
                    emptyMessage="No invoices found"
                    isMobile={true}
                    defaultView="cards"
                  />
                </Box>
              )}

              {tabIndex === 2 && (
                <Box sx={{ p: 2 }}>
                  <UnifiedActivityTable
                    items={payoutsTabItems}
                    onView={(item) => console.log("View payout:", item)}
                    isLoading={isPayoutsLoading}
                    emptyMessage="No payout history"
                    isMobile={true}
                    defaultView="cards"
                  />
                </Box>
              )}
            </Card>
          </Box>
        </Box>
        {sharedModals}
      </>
    );
  }

  // ── Desktop ───────────────────────────────────────────────────────────────
  return (
    <>
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
        <BlitzPayHeader
          totalIncomingAmount={totalIncomingAmount}
          spendingInfo={spendingInfo}
          companyInfo={companyInfo}
          onCopyLink={handleCopyLink}
          onOpenVendorDialog={() => setVendorDialogOpen(true)}
        />

        <Box sx={{ px: 4, pb: 4, pt: 1 }}>
          <Box data-tour="payment-dashboard">
            <BlitzPaySummaryCards
              spendingInfo={spendingInfo}
              spendingLoading={spendingLoading}
              pendingItems={pendingItems}
              amountInEscrow={amountInEscrow}
              creatorsPaid={creatorsPaid}
              currentBalance={currentBalance}
              onShowSpendingDetails={() => setSpendingDetailsOpen(true)}
              onShowPendingItems={() => setPendingModalOpen(true)}
              onShowBalanceLedger={() => setBalanceLedgerOpen(true)}
              hasBlitzPayAccount={hasBlitzPayAccount}
              companyInfo={companyInfo}
            />
          </Box>

          {hasBlitzPayAccount &&
            spendingInfo &&
            parseFloat(spendingInfo.total_available_funds) === 0 && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                No funds available. Contact your administrator to add funds.
              </Alert>
            )}

          <Box sx={{ overflow: "hidden" }} data-tour="transaction-history">
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Box
                sx={{
                  px: 3, py: 2, display: "flex", alignItems: "center", gap: 2,
                  cursor: "pointer",
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                }}
                onClick={() => setFiltersOpen(!filtersOpen)}
              >
                <FilterListIcon sx={{ color: "text.secondary" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>Filters & Search</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filtersOpen ? "Click to collapse" : "Click to expand filters"}
                  </Typography>
                </Box>
                {(statusFilter || searchTerm) && (
                  <Chip
                    label="Active Filters" size="small" color="primary"
                    sx={{ fontSize: "0.75rem" }}
                  />
                )}
                <IconButton size="small">
                  {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={filtersOpen}>
                <Box sx={{ px: 3, pb: 3 }}>
                  <BlitzPayFilters
                    searchTerm={searchTerm}    setSearchTerm={setSearchTerm}
                    statusFilter={statusFilter} setStatusFilter={setStatusFilter}
                    startDate={startDate}       setStartDate={setStartDate}
                    endDate={endDate}           setEndDate={setEndDate}
                    onDownloadPDF={downloadPDF}
                    isMobile={false}
                  />
                </Box>
              </Collapse>
            </Box>

            <BlitzPayTabs
              tabIndex={tabIndex}
              onTabChange={(e, v) => setTabIndex(v)}
              totalIncomingAmount={totalIncomingAmount}
            />

            <Box sx={{ minHeight: 400 }}>
              {tabIndex === 0 && (
                <Box sx={{ p: 3 }}>
                  <UnifiedActivityTable
                    items={filteredCombinedActivity}
                    onEdit={handleEditDialogOpen}
                    onApprove={(itemOrId) =>
                      handleApproveAction(itemOrId, filteredCombinedActivity)
                    }
                    onView={viewHandler}
                    onMerge={handleMergeBlitzPays}
                    isMerging={isMerging}
                    isLoading={activityLoading}
                    isMobile={false}
                    defaultView="cards"
                  />
                </Box>
              )}

              {tabIndex === 1 && (
                <Box sx={{ p: 3 }}>
                  {totalIncomingAmount > 0 && (
                    <Box
                      sx={{
                        mb: 2.5, p: 2, borderRadius: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                        display: "flex", alignItems: "center", gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 36, height: 36, borderRadius: 2,
                          bgcolor: alpha(theme.palette.info.main, 0.15),
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <InfoIcon sx={{ color: "info.main", fontSize: 20 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                          {totalIncomingAmount} invoice{totalIncomingAmount !== 1 ? "s" : ""} awaiting approval
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Review and approve incoming invoices to process payments
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  <UnifiedActivityTable
                    items={combinedInvoicesForInvoicesTab}
                    onEdit={handleEditDialogOpen}
                    onApprove={handleUnifiedInvoiceAction}
                    onView={handleUnifiedInvoiceAction}
                    onMerge={handleMergeBlitzPays}
                    isMerging={isMerging}
                    emptyMessage="No invoices found"
                    isMobile={false}
                    defaultView="cards"
                  />
                </Box>
              )}

              {tabIndex === 2 && (
                <Box sx={{ p: 3 }}>
                  <UnifiedActivityTable
                    items={payoutsTabItems}
                    onView={(item) => console.log("View payout details:", item)}
                    isLoading={isPayoutsLoading}
                    emptyMessage="No payout history found"
                    isMobile={false}
                    defaultView="cards"
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {sharedModals}
    </>
  );
};

export default BlitzPay;